// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { CPT, createReference, formatHumanName, getReferenceString, type MedplumClient } from '@medplum/core';
import type { ChargeItem, Encounter, Patient, Practitioner, Resource, Schedule, Slot } from '@medplum/fhirtypes';
import type { AppointmentTypeDefinition, BookAppointmentResult } from '@polarissleep/scheduling';
import {
  ensurePractitionerOnCareTeam,
  REPLACES_APPOINTMENT_EXTENSION_URL,
  resolveChargeForAppointmentType,
} from '@polarissleep/scheduling';
import type { ProposedAppointment, SchedulingTarget } from '@polarissleep/scheduling-next';
import { SCHEDULING_V2_PROVENANCE_TAG, withTenantAndEnvironmentScope } from '@polarissleep/scheduling-next';
import {
  APPOINTMENT_CHARGE_APPOINTMENT_SYSTEM,
  isBundleCoveredAppointment,
  isSelfPayPatient,
} from '@polarissleep/shared';
import * as Sentry from '@sentry/react';
import { getProviderAppEnvConfig } from '../config/env';
import { botIdentifier } from '../lib/bot-identifier';
import { createProviderSchedulingClient, getProviderSchedulingScope } from '../scheduling-next/client';
import type { ProviderAppointmentCandidate } from '../scheduling-next/types';

export async function bookProviderV2Appointment(
  medplum: MedplumClient,
  params: {
    patient: Patient;
    candidate: ProviderAppointmentCandidate;
    appointmentType: AppointmentTypeDefinition;
    replacesAppointmentId?: string;
  }
): Promise<BookAppointmentResult> {
  const scope = getProviderSchedulingScope();
  const target = await resolveTarget(medplum, params.candidate, scope);
  const appointment = await createProviderSchedulingClient(medplum).book({
    appointment: addBookingData(
      params.candidate.appointment,
      params.patient,
      params.appointmentType,
      params.replacesAppointmentId
    ),
    target,
  });
  let encounterId = '';
  let requiresPayment = false;
  try {
    const encounter = await medplum.createResource(
      withTenantAndEnvironmentScope(
        {
          resourceType: 'Encounter',
          status: 'planned',
          class: {
            system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
            code: params.appointmentType.mode === 'virtual' ? 'VR' : 'AMB',
            display: params.appointmentType.mode === 'virtual' ? 'virtual' : 'ambulatory',
          },
          type: [{ coding: [params.appointmentType.coding], text: params.appointmentType.display }],
          subject: createReference(params.patient),
          appointment: [createReference(appointment.appointment)],
          participant: [{ individual: { reference: `Practitioner/${target.actor.id}` } }],
        } satisfies Encounter,
        scope.organizationId,
        scope.environment
      )
    );
    encounterId = encounter.id ?? '';
    const bundleCovered = await isBundleCoveredAppointment(
      medplum,
      params.patient,
      params.appointmentType.coding.code ?? '',
      { appointmentStart: appointment.appointment.start }
    );
    const config = getProviderAppEnvConfig();
    const chargeSpec =
      bundleCovered || (config.insuranceDeferredBillingEnabled && !isSelfPayPatient(params.patient))
        ? null
        : await resolveChargeForAppointmentType(
            medplum,
            params.appointmentType,
            params.patient,
            appointment.appointment.start ?? new Date().toISOString()
          );
    requiresPayment = Boolean(chargeSpec);
    if (chargeSpec && encounter.id && appointment.appointment.id) {
      await medplum.createResource(
        withTenantAndEnvironmentScope(
          {
            resourceType: 'ChargeItem',
            status: 'planned',
            subject: createReference(params.patient),
            context: createReference(encounter),
            service: [createReference(appointment.appointment)] as unknown as ChargeItem['service'],
            code: { coding: [{ system: CPT, code: chargeSpec.cptCode, display: params.appointmentType.display }] },
            definitionCanonical: [chargeSpec.chargeItemDefinitionUrl],
            occurrenceDateTime: appointment.appointment.start,
            identifier: [{ system: APPOINTMENT_CHARGE_APPOINTMENT_SYSTEM, value: appointment.appointment.id }],
            priceOverride: { value: chargeSpec.amountUsd, currency: 'USD' },
            quantity: { value: 1 },
          } satisfies ChargeItem,
          scope.organizationId,
          scope.environment
        )
      );
    }
    if (target.actor.resourceType !== 'Practitioner') {
      throw new Error('Provider v2 booking requires a Practitioner schedule actor.');
    }
    await ensurePractitionerOnCareTeam(medplum, params.patient, target.actor, {
      decorate: <T extends { resourceType: string }>(resource: T): T =>
        withTenantAndEnvironmentScope(resource as Resource, scope.organizationId, scope.environment) as T,
    });
  } catch (error) {
    Sentry.captureException(error, {
      extra: { appointmentId: appointment.appointment.id, operation: 'provider-v2-book-finalization' },
    });
  }
  try {
    await medplum.executeBot(
      botIdentifier('create-zoom-meetings'),
      { appointmentId: appointment.appointment.id },
      'application/json'
    );
  } catch (error) {
    Sentry.captureException(error, {
      extra: { appointmentId: appointment.appointment.id, operation: 'provider-v2-zoom-finalization' },
    });
  }
  return { appointmentId: appointment.appointment.id, encounterId, requiresPayment };
}

async function resolveTarget(
  medplum: MedplumClient,
  candidate: ProviderAppointmentCandidate,
  scope: ReturnType<typeof getProviderSchedulingScope>
): Promise<SchedulingTarget> {
  const slot = candidate.appointment.contained?.find((resource): resource is Slot => resource.resourceType === 'Slot');
  const scheduleId = slot?.schedule?.reference?.startsWith('Schedule/') ? slot.schedule.reference.slice(9) : '';
  if (!scheduleId || !candidate.serviceId || !candidate.providerId) {
    throw new Error('Selected appointment is missing scheduling resources.');
  }
  const [healthcareService, schedule, actor] = await Promise.all([
    medplum.readResource('HealthcareService', candidate.serviceId),
    medplum.readResource('Schedule', scheduleId) as Promise<Schedule>,
    medplum.readResource('Practitioner', candidate.providerId) as Promise<Practitioner>,
  ]);
  return { healthcareService, schedule, actor, scope };
}

function addBookingData(
  appointment: ProposedAppointment,
  patient: Patient,
  appointmentType: AppointmentTypeDefinition,
  replacesAppointmentId?: string
): ProposedAppointment {
  const patientReference = getReferenceString(patient);
  return {
    ...appointment,
    meta: {
      ...appointment.meta,
      tag: [
        ...(appointment.meta?.tag ?? []).filter((tag) => tag.system !== SCHEDULING_V2_PROVENANCE_TAG.system),
        SCHEDULING_V2_PROVENANCE_TAG,
      ],
    },
    appointmentType: { coding: [appointmentType.coding], text: appointmentType.display },
    ...(replacesAppointmentId
      ? {
          extension: [
            ...(appointment.extension ?? []).filter(
              (extension) => extension.url !== REPLACES_APPOINTMENT_EXTENSION_URL
            ),
            {
              url: REPLACES_APPOINTMENT_EXTENSION_URL,
              valueReference: { reference: `Appointment/${replacesAppointmentId}` },
            },
          ],
        }
      : {}),
    participant: [
      ...(appointment.participant ?? []),
      ...(appointment.participant?.some((participant) => participant.actor?.reference === patientReference)
        ? []
        : [
            {
              actor: { reference: patientReference, display: formatHumanName(patient.name?.[0] ?? {}) },
              status: 'accepted' as const,
            },
          ]),
    ],
  };
}
